import * as sharedProgress from '../../functions/src/shared/targetInvestorProgress.js';

export const TARGET_INVESTOR_PROGRESS_OPTIONS = sharedProgress.CANONICAL_PROGRESS_OPTIONS;
export const TARGET_INVESTOR_PROGRESS_VALUES = sharedProgress.CANONICAL_PROGRESS_VALUES;
export const TARGET_INVESTOR_LEGACY_PROGRESS_OPTIONS = sharedProgress.LEGACY_PROGRESS_OPTIONS;
export const TARGET_INVESTOR_LEGACY_PROGRESS_VALUES = sharedProgress.LEGACY_PROGRESS_VALUES;
export const TARGET_INVESTOR_PROGRESS_MIGRATION_MAP =
  sharedProgress.DETERMINISTIC_MIGRATION_MAP;

export type TargetInvestorProgress =
  | 'not_contacted'
  | 'outreach_sent'
  | 'contact_established'
  | 'information_requested'
  | 'initial_interest'
  | 'meeting_scheduled'
  | 'meeting_completed'
  | 'under_review'
  | 'due_diligence'
  | 'terms_discussion'
  | 'committed'
  | 'invested'
  | 'passed'
  | 'unresponsive'
  | 'on_hold';

export type TargetInvestorLegacyProgress =
  | 'not_sent'
  | 'linkedin_request'
  | 'email_sent'
  | 'more_info'
  | 'interested'
  | 'in_play'
  | 'investing';

export type TargetInvestorProgressClassification =
  | { kind: 'canonical'; value: TargetInvestorProgress; label: string; raw: unknown }
  | { kind: 'legacy'; value: TargetInvestorLegacyProgress; label: string; raw: unknown }
  | { kind: 'missing'; value: null; label: 'Not set'; raw: unknown }
  | { kind: 'unknown'; value: null; label: string; raw: unknown };

export const classifyTargetInvestorProgress = (
  raw: unknown
): TargetInvestorProgressClassification =>
  sharedProgress.classifyProgress(raw) as TargetInvestorProgressClassification;

export const formatTargetInvestorProgress = (raw: unknown): string =>
  sharedProgress.formatProgress(raw);

export const getTargetInvestorProgressDataState = (raw: unknown): string =>
  sharedProgress.progressDataState(raw);

export const getTargetInvestorProgressFilterKey = (raw: unknown): string =>
  sharedProgress.progressFilterKey(raw);

export const isTargetInvestorProgress = (
  raw: unknown
): raw is TargetInvestorProgress => sharedProgress.isCanonicalProgress(raw);

export const isLegacyTargetInvestorProgress = (
  raw: unknown
): raw is TargetInvestorLegacyProgress => sharedProgress.isLegacyProgress(raw);

/** True once Progress has moved off No Contact (or legacy Not Sent). */
export const isOutreachStartedProgress = (raw: unknown): boolean =>
  sharedProgress.isOutreachStartedProgress(raw);

export const resolveTargetInvestorEmailProgress = (
  raw: unknown
): { value: unknown; changed: boolean; previousValue: unknown } =>
  sharedProgress.resolveEmailProgress(raw);

export const resolveLegacyTargetInvestorEmailProgress = (
  raw: unknown
): { value: unknown; changed: boolean; previousValue: unknown } =>
  sharedProgress.resolveLegacyEmailProgress(raw);

export type SyndicationProgressShape = {
  kind: 'flat' | 'nested' | 'dual_equal' | 'dual_conflict' | 'missing';
  value: unknown;
  flatPresent: boolean;
  nestedPresent: boolean;
  flatValue: unknown;
  nestedValue: unknown;
};

export const resolveSyndicationTargetInvestorProgressShape = (
  data: unknown
): SyndicationProgressShape =>
  sharedProgress.resolveSyndicationProgressShape(data) as SyndicationProgressShape;

export const mapStage4ToTargetInvestorProgress = (
  raw: unknown
): TargetInvestorProgress => sharedProgress.mapStage4ToProgress(raw);

export const isTargetInvestorCommittedProgress = (raw: unknown): boolean =>
  sharedProgress.isCommittedProgress(raw);

export const isTargetInvestorVerbalProgress = (raw: unknown): boolean =>
  sharedProgress.isVerbalProgress(raw);

export const isTargetInvestorEngagedProgress = (raw: unknown): boolean =>
  sharedProgress.isEngagedProgress(raw);

export const isTargetInvestorPreEngagementProgress = (raw: unknown): boolean =>
  sharedProgress.isPreEngagementProgress(raw);

/** Active filter bucket = engaged (Info Request → Invested). */
export const isTargetInvestorActiveProgress = (raw: unknown): boolean =>
  sharedProgress.isActiveProgress(raw);

export const isTargetInvestorPausedProgress = (raw: unknown): boolean =>
  sharedProgress.isPausedProgress(raw);

export const isTargetInvestorPassedProgress = (raw: unknown): boolean =>
  sharedProgress.isPassedProgress(raw);

export const isTargetInvestorOutreachStartedProgress = (raw: unknown): boolean =>
  sharedProgress.isOutreachStartedProgress(raw);

/** FR-26 Matured: In Review / In DD / Terms / Committed / Invested. Value keys, not labels. */
const MATURED_PROGRESS_VALUES = new Set<TargetInvestorProgress>([
  'under_review',
  'due_diligence',
  'terms_discussion',
  'committed',
  'invested',
]);

export const isTargetInvestorMaturedProgress = (raw: unknown): boolean => {
  const classified = classifyTargetInvestorProgress(raw);
  return classified.kind === 'canonical' && MATURED_PROGRESS_VALUES.has(classified.value);
};

export const planTargetInvestorProgressMigration = (raw: unknown) =>
  sharedProgress.planProgressMigration(raw);
